'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Volume2 } from 'lucide-react'
import type { LandingContent } from '@/types/landing'

const muxPlayerCommand = (method: 'unmute' | 'play') => JSON.stringify({
  context: 'player.js',
  version: '0.0.11',
  method,
})

export default function LaunchFilm({ content }: { content: LandingContent['film'] }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const nearViewportRef = useRef(false)
  const [eligible, setEligible] = useState(false)
  const [showUnmute, setShowUnmute] = useState(false)
  const [constrainedPlayback, setConstrainedPlayback] = useState(true)
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  const playerSrc = useMemo(() => {
    try {
      const url = new URL(content.src)
      url.searchParams.set('autoplay', 'muted')
      url.searchParams.set('muted', 'true')
      url.searchParams.set('default-hidden-captions', 'false')
      url.searchParams.set('max-resolution', '2160p')
      url.searchParams.set('rendition-order', 'desc')
      url.searchParams.set('cap-rendition-to-player-size', 'false')
      return url.toString()
    } catch {
      return content.src
    }
  }, [content.src])
  const poster = useMemo(() => {
    try {
      const playbackId = new URL(content.src).pathname.split('/').filter(Boolean)[0]
      return playbackId
        ? {
            mobile: `https://image.mux.com/${playbackId}/thumbnail.webp?time=43&width=800&fit_mode=preserve`,
            desktop: `https://image.mux.com/${playbackId}/thumbnail.webp?time=43&width=1600&fit_mode=preserve`,
          }
        : null
    } catch {
      return null
    }
  }, [content.src])

  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 700px)')
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    const sync = () => setConstrainedPlayback(mobile.matches || Boolean(connection?.saveData))
    sync()
    mobile.addEventListener('change', sync)
    return () => mobile.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const syncPreference = () => {
      setReducedMotion(preference.matches)
      if (preference.matches) setEligible(false)
    }

    syncPreference()
    preference.addEventListener('change', syncPreference)
    return () => preference.removeEventListener('change', syncPreference)
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!root || reducedMotion || constrainedPlayback) return

    const observer = new IntersectionObserver(([entry]) => {
      nearViewportRef.current = entry.isIntersecting
      if (entry.isIntersecting && document.visibilityState !== 'hidden') {
        setEligible(true)
      }
    }, { rootMargin: '240px 0px', threshold: 0.01 })

    observer.observe(root)
    return () => observer.disconnect()
  }, [constrainedPlayback, reducedMotion])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        setEligible(false)
      } else if (nearViewportRef.current && !reducedMotion && !constrainedPlayback) {
        setEligible(true)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [constrainedPlayback, reducedMotion])

  useEffect(() => {
    if (!eligible) {
      setShowUnmute(false)
    }
  }, [eligible])

  const handleUnmute = () => {
    const playerWindow = iframeRef.current?.contentWindow
    if (!playerWindow) return

    playerWindow.postMessage(muxPlayerCommand('unmute'), 'https://player.mux.com')
    playerWindow.postMessage(muxPlayerCommand('play'), 'https://player.mux.com')
    setShowUnmute(false)
  }

  return <section className="fp-film-section" aria-label={content.label}>
    <div ref={rootRef} className="fp-film-card">
      {eligible ? <iframe
        ref={iframeRef}
        src={playerSrc}
        title={content.title}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        onLoad={() => setShowUnmute(true)}
      /> : <button
        type="button"
        className="fp-film-facade"
        onClick={() => setEligible(true)}
        aria-label="Play launch film"
      >
        {poster && <picture className="fp-film-poster">
          <source media="(max-width: 700px)" srcSet={poster.mobile} />
          <img src={poster.desktop} alt="" fetchPriority="high" />
        </picture>}
        <span aria-hidden="true">▶</span><strong>Play launch film</strong>
      </button>}
      {showUnmute && <button
        type="button"
        className="fp-film-unmute"
        onClick={handleUnmute}
        aria-label="Unmute launch film"
      >
        <Volume2 aria-hidden="true" />
        <span>Unmute</span>
      </button>}
    </div>
  </section>
}
